import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    ChangeDetectionStrategy,
    Output,
    EventEmitter,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25TableModule } from "../../../s25-table/s25.table.module";
import { Table } from "../../../s25-table/Table";
import { S25EditableModule } from "../../../s25-editable/s25.editable.module";
import { BudgetService } from "../budget.service";
import { Budget } from "../Budget";
import { S25Util } from "../../../../util/s25-util";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { GenericTableButtonComponent } from "../../../s25-table/generics/generic.table.button.component";
import { UserprefService } from "../../../../services/userpref.service";

@TypeManagerDecorator("s25-ng-fiscal-year-management")
@Component({
    selector: "s25-ng-fiscal-year-management",
    template: `
        <div class="fiscal-year-management-container">
            <div class="management-header">
                <h3>Fiscal Year Management</h3>
                <s25-ng-button
                    [type]="'primary'"
                    [buttonText]="'+ Add Fiscal Year'"
                    [onClick]="startAddFiscalYear"
                    [disabled]="isProcessing()"
                ></s25-ng-button>
            </div>

            <!-- <s25-ng-fading-success-msg [message]="successMessage"></s25-ng-fading-success-msg> -->
            @if (isLoading()) {
                <s25-ng-loading-inline-static [text]="'Loading fiscal years...'"></s25-ng-loading-inline-static>
            }

            <!-- Error State -->
            @if (error) {
                <div class="error-state">
                    <p class="error-message">{{ error }}</p>
                    <s25-ng-button
                        [type]="'outline'"
                        [buttonText]="'Retry'"
                        [onClick]="loadFiscalYears"
                    ></s25-ng-button>
                </div>
            }

            <!-- Fiscal Year Form Modal -->
            @if (showForm()) {
                <div class="modal-overlay" (click)="cancelForm()">
                    <div class="modal-content" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>{{ isEditing() ? "Edit Fiscal Year" : "Add New Fiscal Year" }}</h4>
                            <button class="close-button" (click)="cancelForm()" type="button">×</button>
                        </div>

                        <form class="fiscal-year-form" (ngSubmit)="saveFiscalYear()" #fiscalYearForm="ngForm">
                            <div class="form-group">
                                <label for="fiscalYearName">Fiscal Year Name *</label>
                                <input
                                    id="fiscalYearName"
                                    type="text"
                                    [(ngModel)]="formData.name"
                                    name="fiscalYearName"
                                    required
                                    maxlength="100"
                                    class="form-input"
                                    placeholder="e.g., 2024-2025"
                                />
                            </div>

                            <div class="form-group">
                                <label for="fiscalYearDescription">Description</label>
                                <textarea
                                    id="fiscalYearDescription"
                                    [(ngModel)]="formData.description"
                                    name="fiscalYearDescription"
                                    class="form-textarea"
                                    placeholder="Optional description"
                                    rows="3"
                                ></textarea>
                            </div>

                            <div class="form-row">
                                <div class="form-group">
                                    <label for="startDate">Start Date *</label>
                                    <s25-ng-editable-date
                                        [(val)]="formData.startDate"
                                        [placeholder]="'Select start date'"
                                        class="date-input"
                                    ></s25-ng-editable-date>
                                </div>

                                <div class="form-group">
                                    <label for="endDate">End Date *</label>
                                    <s25-ng-editable-date
                                        [(val)]="formData.endDate"
                                        [placeholder]="'Select end date'"
                                        class="date-input"
                                    ></s25-ng-editable-date>
                                </div>
                            </div>

                            <div class="form-actions">
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Cancel'"
                                    [onClick]="cancelForm"
                                    [disabled]="isProcessing()"
                                ></s25-ng-button>
                                <s25-ng-button
                                    [type]="'primary'"
                                    [buttonText]="isEditing() ? 'Update Fiscal Year' : 'Create Fiscal Year'"
                                    [onClick]="saveFiscalYear"
                                    [isLoading]="isProcessing()"
                                    [disabled]="!fiscalYearForm.valid"
                                ></s25-ng-button>
                            </div>
                        </form>
                    </div>
                </div>
            }

            <!-- Fiscal Years Table -->
            @if (!isLoading() && !error) {
                <s25-ng-table
                    [dataSource]="fiscalYearTableData"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                    [hasTotalRowCount]="true"
                    [caption]="'Fiscal Years'"
                    [noResultsMessage]="
                        'No fiscal years have been created yet. Click Add Fiscal Year to create your first fiscal year.'
                    "
                ></s25-ng-table>
            }

            <!-- Delete Confirmation Modal -->
            @if (showDeleteConfirm()) {
                <div class="modal-overlay" (click)="cancelDelete()">
                    <div class="modal-content small" (click)="$event.stopPropagation()">
                        <div class="modal-header">
                            <h4>Confirm Delete</h4>
                        </div>
                        <div class="modal-body">
                            <p>Are you sure you want to delete the fiscal year "{{ fiscalYearToDelete()?.name }}"?</p>
                            <p class="warning-text">This action cannot be undone.</p>
                        </div>
                        <div class="modal-actions">
                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Cancel'"
                                [onClick]="cancelDelete"
                            ></s25-ng-button>
                            <s25-ng-button
                                [type]="'danger'"
                                [buttonText]="'Delete'"
                                [onClick]="deleteFiscalYear"
                                [isLoading]="isProcessing()"
                            ></s25-ng-button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .fiscal-year-management-container {
            padding: 1.5rem;
        }

        .management-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .management-header h3 {
            margin: 0;
            color: #333;
            font-size: 1.25rem;
            font-weight: 600;
        }

        .error-state {
            text-align: center;
            padding: 2rem;
            background: #fff3f3;
            border: 1px solid #ffcdd2;
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message {
            color: #d32f2f;
            margin-bottom: 1rem;
        }

        /* Modal Styles */
        .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
        }

        .modal-content {
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            max-width: 600px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
        }

        .modal-content.small {
            max-width: 400px;
        }

        .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.5rem;
            border-bottom: 1px solid #e5e5e5;
        }

        .modal-header h4 {
            margin: 0;
            color: #333;
            font-size: 1.1rem;
            font-weight: 600;
        }

        .close-button {
            background: none;
            border: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .close-button:hover {
            color: #333;
        }

        /* Form Styles */
        .fiscal-year-form {
            padding: 1.5rem;
        }

        .form-group {
            margin-bottom: 1rem;
        }

        .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #333;
        }

        .form-input,
        .form-textarea,
        .form-select {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 1rem;
            transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
            outline: none;
            border-color: #2196f3;
            box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
        }

        .checkbox-label {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }

        .form-checkbox {
            width: 18px;
            height: 18px;
            cursor: pointer;
        }

        .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 1.5rem;
            padding-top: 1rem;
            border-top: 1px solid #e5e5e5;
        }

        /* Modal Body and Actions */
        .modal-body {
            padding: 1.5rem;
        }

        .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            padding: 1rem 1.5rem;
            border-top: 1px solid #e5e5e5;
        }

        .warning-text {
            color: #f44336;
            font-weight: 500;
            margin-top: 0.5rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            .fiscal-year-management-container {
                padding: 1rem;
            }

            .management-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .modal-content {
                width: 95%;
                margin: 1rem;
            }

            .form-row {
                grid-template-columns: 1fr;
            }
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25ButtonComponent, S25LoadingInlineModule, S25TableModule, S25EditableModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiscalYearManagementComponent implements OnInit {
    @Output() fiscalYearsChanged = new EventEmitter<void>();

    isLoading = signal(false);
    isProcessing = signal(false);
    showForm = signal(false);
    isEditing = signal(false);
    showDeleteConfirm = signal(false);

    fiscalYears = signal<Budget.FiscalYear[]>([]);
    fiscalYearToDelete = signal<Budget.FiscalYear | null>(null);
    s25DateTimeFormat: string = "MM/dd/yyyy";
    fiscalYearTableData: Table.DataSource;
    successMessage: string = "";
    error: string | null = null;

    formData: Partial<Budget.FiscalYear> = {
        name: "",
        description: "",
        startDate: undefined,
        endDate: undefined,
    };

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit(): Promise<void> {
        this.loadFiscalYears();
        await this.initializeTableData();
    }

    private async initializeTableData(): Promise<void> {
        this.s25DateTimeFormat = await UserprefService.getS25Dateformat();

        this.fiscalYearTableData = {
            type: "unpaginated",
            dataSource: this.getFiscalYearTableRows,
            columns: [
                {
                    id: "name",
                    header: "Fiscal Year",
                    minWidth: 200,
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "dateRange",
                    header: "Date Range",
                    minWidth: 220,
                    cells: {
                        text: "",
                    },
                },
                {
                    id: "edit",
                    header: "Edit",
                    width: 70,
                    align: "center",
                    sortable: false,
                    cells: {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: "Edit",
                            type: "outline",
                        },
                    },
                },
                {
                    id: "delete",
                    header: "Delete",
                    width: 70,
                    align: "center",
                    sortable: false,
                    cells: {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: "Delete",
                            type: "danger--outline",
                        },
                    },
                },
            ],
        };
    }

    @Bind
    async getFiscalYearTableRows(): Promise<Table.DataSourceResponse> {
        CacheRepository.invalidateByService("BudgetService", "getFiscalYears");
        const fiscalYears = this.fiscalYears();
        const rows: Table.Row[] = [];

        for (const fiscalYear of fiscalYears) {
            rows.push(this.createFiscalYearRow(fiscalYear));
        }

        return {
            rows,
            totalRows: rows.length,
        };
    }

    private createFiscalYearRow(fiscalYear: Budget.FiscalYear): Table.Row {
        return {
            id: fiscalYear.fiscalYearId,
            name: fiscalYear.name,
            data: fiscalYear,
            cells: {
                name: {
                    text: fiscalYear.name,
                },
                dateRange: {
                    text: S25Util.date.fromToString(fiscalYear.startDate, fiscalYear.endDate, this.s25DateTimeFormat),
                },
                edit: {
                    inputs: {
                        label: "Edit",
                        onClick: () => this.startEditFiscalYear(fiscalYear),
                    },
                },
                delete: {
                    inputs: {
                        label: "Delete",
                        onClick: () => this.confirmDeleteFiscalYear(fiscalYear),
                    },
                },
            },
        };
    }

    async loadFiscalYears(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;

        try {
            // Invalidate cache to ensure fresh data
            CacheRepository.invalidateByService("BudgetService", "getFiscalYears");
            const [fiscalYears, error] = await S25Util.Maybe(BudgetService.getFiscalYears());

            if (error) {
                this.error = "Failed to load fiscal years. Please try again.";
                console.error("Error loading fiscal years:", error);
            } else {
                this.fiscalYears.set(fiscalYears || []);
            }
        } catch (error) {
            this.error = "An unexpected error occurred while loading fiscal years.";
            console.error("Unexpected error:", error);
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    startAddFiscalYear = () => {
        this.resetForm();
        this.isEditing.set(false);
        this.showForm.set(true);
    };

    startEditFiscalYear = (fiscalYear: Budget.FiscalYear) => {
        this.formData = {
            ...fiscalYear,
            startDate: S25Util.date.toS25ISODateTimeStr(fiscalYear.startDate),
            endDate: S25Util.date.toS25ISODateTimeStr(fiscalYear.endDate),
        };
        this.isEditing.set(true);
        this.showForm.set(true);
        this.cd.detectChanges();
    };

    @Bind
    async saveFiscalYear(): Promise<void> {
        if (this.isProcessing()) return;
        this.isProcessing.set(true);
        this.cd.detectChanges();

        try {
            const fiscalYearData: Budget.FiscalYear = {
                fiscalYearId: this.formData.fiscalYearId!,
                name: this.formData.name!,
                description: this.formData.description,
                startDate: S25Util.date.toS25ISODateTimeStr(this.formData.startDate!)!,
                endDate: S25Util.date.toS25ISODateTimeStr(this.formData.endDate!)!,
            };

            if (this.isEditing()) {
                const [result, error] = await S25Util.Maybe(
                    BudgetService.updateFiscalYear(this.formData.fiscalYearId!, fiscalYearData),
                );
                if (error) throw error;
                this.successMessage = "Fiscal year updated successfully";
            } else {
                const [result, error] = await S25Util.Maybe(BudgetService.createFiscalYear(fiscalYearData));
                if (error) throw error;
                this.successMessage = "Fiscal year created successfully";
            }

            await this.loadFiscalYears();
            this.showForm.set(false);
            this.fiscalYearsChanged.emit();
        } catch (error) {
            S25Util.showError(`Failed to save fiscal year: ${S25Util.errorText(error)}`);
        } finally {
            this.isProcessing.set(false);
            this.cd.detectChanges();
        }
    }

    cancelForm = () => {
        this.showForm.set(false);
        this.resetForm();
    };

    confirmDeleteFiscalYear = (fiscalYear: Budget.FiscalYear) => {
        this.fiscalYearToDelete.set(fiscalYear);
        this.showDeleteConfirm.set(true);
        this.cd.detectChanges();
    };

    @Bind
    async deleteFiscalYear(): Promise<void> {
        const fiscalYear = this.fiscalYearToDelete();
        if (!fiscalYear || this.isProcessing()) return;

        this.isProcessing.set(true);
        this.cd.detectChanges();

        try {
            const [result, error] = await S25Util.Maybe(BudgetService.deleteFiscalYear(fiscalYear.fiscalYearId));
            if (error) throw error;

            this.successMessage = "Fiscal year deleted successfully";
            await this.loadFiscalYears();
            this.showDeleteConfirm.set(false);
            this.fiscalYearToDelete.set(null);
            this.fiscalYearsChanged.emit();
        } catch (error) {
            S25Util.showError(`Failed to delete fiscal year: ${S25Util.errorText(error)}`);
        } finally {
            this.isProcessing.set(false);
            this.cd.detectChanges();
        }
    }

    cancelDelete = () => {
        this.showDeleteConfirm.set(false);
        this.fiscalYearToDelete.set(null);
    };

    private resetForm(): void {
        this.formData = {
            name: "",
            description: "",
            startDate: undefined,
            endDate: undefined,
        };
    }
}
